"use client";
import { jwtDecode } from "jwt-decode";
import { useRouter } from "next/navigation";
import { useMSALContext } from "../../../utils/auth/MSALContextProvider/useMSALContext.js";
import { isWcsSessionEstablished } from "../../../utils/auth/wcsToken/index.js";
import { useCallback } from "react";
import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
const useMSALUtils = () => {
  var _a, _b, _c, _d, _e, _f, _g;
  const {
    msalInstance,
    baseMsalConfig,
    msalEnabledFeatureFlag,
    msalInstanceLoaded
  } = useMSALContext() || {};
  const router = useRouter();
  const handleMsalError = useCallback((error, context) => {
    if (error instanceof Error) {
      if (error.message.includes("AADB2C90091")) {
        console.log("MSAL Error:: User canceled the operation.");
      } else {
        console.error(`MSAL Error:: ${context}: `, error);
      }
    } else {
      console.error("MSAL Error:: Unknown error:", error);
    }
  }, []);
  const handleRedirectPromise = useCallback(async () => {
    var _a2, _b2, _c2, _d2, _e2, _f2;
    if (msalInstance && msalInstanceLoaded) {
      try {
        const response = await msalInstance.handleRedirectPromise();
        const isEditPass = sessionStorage.getItem("editPassProgress");
        const isEditEmail = sessionStorage.getItem("editEmailProgress");
        if (isEditPass || isEditEmail) {
          let newEmail;
          if (response == null ? void 0 : response.idToken) {
            const decodedToken = jwtDecode(
              response.idToken
            );
            newEmail = ((_a2 = decodedToken == null ? void 0 : decodedToken.emails) == null ? void 0 : _a2[0]) || (typeof (decodedToken == null ? void 0 : decodedToken.email) === "string" ? decodedToken.email : void 0);
          }
          if (!newEmail) {
            const activeAccount = msalInstance.getActiveAccount();
            newEmail = activeAccount == null ? void 0 : activeAccount.username;
          }
          const encodedEmail = newEmail ? encodeURIComponent(newEmail) : void 0;
          const extraQueryParameters = {
            prompt: "login",
            // Force login screen
            login_hint: encodedEmail,
            // Pre-fill with new email
            ClientName: (_c2 = (_b2 = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _b2.auth) == null ? void 0 : _c2.clientName,
            TC: (_e2 = (_d2 = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _d2.auth) == null ? void 0 : _e2.tc,
            ui_locales: (_f2 = baseMsalConfig == null ? void 0 : baseMsalConfig.extraQueryParameters) == null ? void 0 : _f2.ui_locales
          };
          if (isEditPass) {
            sessionStorage.removeItem("editPassProgress");
            extraQueryParameters.isEditPassword = "true";
          }
          if (isEditEmail) {
            sessionStorage.removeItem("editEmailProgress");
            extraQueryParameters.isEditEmail = "true";
          }
          const loginRequest = {
            scopes: ["openid", "profile"],
            extraQueryParameters
          };
          await msalInstance.loginRedirect(
            loginRequest
          );
          return null;
        }
        const account = (response == null ? void 0 : response.account) ? response.account : msalInstance.getAllAccounts()[0];
        if (account) {
          msalInstance.setActiveAccount(account);
          return account;
        }
      } catch (error) {
        sessionStorage.removeItem("editPassProgress");
        sessionStorage.removeItem("editEmailProgress");
        handleMsalError(error, "Redirect error");
      }
    } else {
      console.error(
        "MSAL Error:: msalInstance is not defined at handleRedirectPromise"
      );
    }
    return null;
  }, [
    handleMsalError,
    msalInstance,
    msalInstanceLoaded,
    (_b = (_a = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _a.auth) == null ? void 0 : _b.clientName,
    (_d = (_c = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _c.auth) == null ? void 0 : _d.tc,
    (_e = baseMsalConfig == null ? void 0 : baseMsalConfig.extraQueryParameters) == null ? void 0 : _e.ui_locales
  ]);
  const signout = useCallback(async () => {
    var _a2, _b2, _c2, _d2, _e2, _f2, _g2, _h, _i, _j;
    if (msalInstance && msalInstanceLoaded) {
      try {
        if (typeof window !== "undefined") {
          fetch(`${window.location.origin}/Logoff`);
        }
        const account = msalInstance.getActiveAccount();
        await msalInstance.logoutRedirect({
          account,
          postLogoutRedirectUri: (_a2 = window == null ? void 0 : window.location) == null ? void 0 : _a2.origin,
          authority: (_c2 = (_b2 = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _b2.auth) == null ? void 0 : _c2.authority,
          extraQueryParameters: {
            ClientName: (_e2 = (_d2 = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _d2.auth) == null ? void 0 : _e2.clientName,
            TC: (_g2 = (_f2 = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _f2.auth) == null ? void 0 : _g2.tc,
            ui_locales: (_h = baseMsalConfig == null ? void 0 : baseMsalConfig.extraQueryParameters) == null ? void 0 : _h.ui_locales,
            client_id: (_j = (_i = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _i.auth) == null ? void 0 : _j.clientId
          }
        });
      } catch (error) {
        handleMsalError(error, "Signout error");
      }
    } else {
      console.error(
        "MSAL Error:: msalInstance is not defined at signout"
      );
    }
    return null;
  }, [handleMsalError, msalInstance, msalInstanceLoaded, baseMsalConfig]);
  const signinWithParam = useCallback(
    async (fromPage) => {
      if (msalInstance && baseMsalConfig && msalInstanceLoaded) {
        try {
          const loginRequest = {
            scopes: ["openid", "profile"],
            extraQueryParameters: {
              ...baseMsalConfig.extraQueryParameters
            }
          };
          const account = msalInstance.getActiveAccount();
          if (!account) {
            loginRequest.extraQueryParameters = {
              ...loginRequest.extraQueryParameters,
              ...fromPage && { state: fromPage }
            };
            msalInstance.loginRedirect(
              loginRequest
            );
            sessionStorage.setItem("initWcsSession", "false");
          }
        } catch (error) {
          if (error instanceof BrowserAuthError && error.errorCode === "interaction_in_progress") {
            console.error(
              "MSAL SignIn error:: Interaction is currently in progress. Please wait for it to complete."
            );
          } else {
            handleMsalError(error, "Signin error");
          }
        }
      } else {
        console.error(
          "MSAL Error:: msalInstance or msalConfig is not defined at signinWithParam"
        );
      }
      return null;
    },
    [handleMsalError, baseMsalConfig, msalInstance, msalInstanceLoaded]
  );
  const signin = useCallback(async () => {
    signinWithParam(null);
  }, [signinWithParam]);
  const setWCSSession = useCallback(
    async (token, subscriptionKey) => {
      const response = await fetch(
        `/OAuthLogonCmd?id_token=${token}&setKmsi=false`,
        {
          method: "POST",
          headers: { Subscription: subscriptionKey },
          body: JSON.stringify({}),
          credentials: "include"
        }
      );
      if ((response == null ? void 0 : response.status) === 200) {
        return response;
      } else {
        console.error("Failed to set WCS session");
        return null;
      }
    },
    []
  );
  const getTokenSilent = useCallback(
    async (skipSignIn = false) => {
      var _a2, _b2;
      if (msalInstance && msalInstanceLoaded) {
        try {
          const account = msalInstance.getActiveAccount();
          if (!account && !skipSignIn) {
            console.error("No active account! Please sign in.");
            signinWithParam(null);
            return null;
          } else if (!account && skipSignIn) {
            return null;
          }
          const request = {
            scopes: ["openid", "profile"],
            account
          };
          try {
            const response = await msalInstance.acquireTokenSilent(
              request
            );
            const wcsSubscriptionKey = (_b2 = (_a2 = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _a2.wcs) == null ? void 0 : _b2.subscriptionKey;
            const hasWcsSession = await isWcsSessionEstablished();
            const initWcsSession = sessionStorage.getItem("initWcsSession");
            if (process.env.NODE_ENV === "production" && !process.env.NEXT_PUBLIC_DISABLE_WCS_REDIRECT && msalEnabledFeatureFlag && !hasWcsSession && initWcsSession) {
              sessionStorage.removeItem("initWcsSession");
              const setWCSSessionResponse = await setWCSSession(
                response.idToken,
                wcsSubscriptionKey
              );
              if (setWCSSessionResponse && setWCSSessionResponse.url) {
                console.log(
                  "WCS session response:",
                  setWCSSessionResponse
                );
                const domainUrl = typeof window !== "undefined" ? window.location.origin : "";
                const url = setWCSSessionResponse.url;
                const escDomain = domainUrl.replace(
                  /[.*+?^${}()|[\]\\]/g,
                  "\\$&"
                );
                const regexString = `^${escDomain}\\/\\?.*$`;
                const regex = new RegExp(regexString);
                console.log(
                  "Checking if URL matches domain:",
                  regex.test(url)
                );
                if (!regex.test(url)) {
                  console.log(
                    "WCS session URL:",
                    setWCSSessionResponse.url
                  );
                  router.push(setWCSSessionResponse.url);
                }
              }
            }
            return response.idToken;
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              console.error(
                `Silent token acquisition failed, ${skipSignIn ? "redirecting to login" : ""} :`,
                error
              );
              if (!skipSignIn) {
                msalInstance.loginRedirect(
                  request
                );
              }
            } else {
              throw error;
            }
          }
        } catch (error) {
          handleMsalError(error, "Get token silent error");
        }
      } else {
        console.error(
          "MSAL Error:: msalInstance is not defined at getTokenSilent"
        );
      }
      return null;
    },
    [
      msalInstance,
      msalInstanceLoaded,
      msalEnabledFeatureFlag,
      signinWithParam,
      (_g = (_f = baseMsalConfig == null ? void 0 : baseMsalConfig.msalConfig) == null ? void 0 : _f.wcs) == null ? void 0 : _g.subscriptionKey,
      setWCSSession,
      router,
      handleMsalError
    ]
  );
  const editEmail = useCallback(async () => {
    if (msalInstance && msalInstanceLoaded) {
      try {
        const account = msalInstance.getActiveAccount();
        if (account) {
          const editEmailRequest = {
            authority: baseMsalConfig.editEmailAuthority,
            scopes: ["openid", "profile"],
            extraQueryParameters: {
              ...baseMsalConfig.extraQueryParameters
            }
          };
          sessionStorage.setItem("editEmailProgress", "true");
          await msalInstance.loginRedirect(
            editEmailRequest
          );
        } else {
          signinWithParam(null);
          console.error("No active account! Please sign in.");
        }
      } catch (error) {
        handleMsalError(error, "Edit email error");
      }
    } else {
      console.error(
        "MSAL Error:: msalInstance is not defined at editEmail"
      );
    }
    return null;
  }, [
    handleMsalError,
    baseMsalConfig,
    msalInstance,
    msalInstanceLoaded,
    signinWithParam
  ]);
  const editPassword = useCallback(async () => {
    if (msalInstance && msalInstanceLoaded) {
      try {
        const account = msalInstance.getActiveAccount();
        if (account) {
          const editPasswordRequest = {
            authority: baseMsalConfig.editPasswordAuthority,
            scopes: ["openid", "profile"],
            extraQueryParameters: baseMsalConfig.extraQueryParameters
          };
          sessionStorage.setItem("editPassProgress", "true");
          await msalInstance.loginRedirect(
            editPasswordRequest
          );
        } else {
          signinWithParam(null);
          console.error("No active account! Please sign in.");
        }
      } catch (error) {
        handleMsalError(error, "Edit password error");
      }
    } else {
      console.error(
        "MSAL Error:: msalInstance is not defined at editPassword"
      );
    }
    return null;
  }, [
    handleMsalError,
    baseMsalConfig,
    msalInstance,
    msalInstanceLoaded,
    signinWithParam
  ]);
  const isMSALKmsiSession = useCallback(async () => {
    if (msalInstance && msalInstanceLoaded) {
      try {
        const idToken = await getTokenSilent(true);
        if (idToken) {
          const decodedToken = jwtDecode(idToken);
          const rememberMe = decodedToken == null ? void 0 : decodedToken.rememberMe;
          return rememberMe === "True" ? true : false;
        }
      } catch (error) {
        console.error("MSAL Error: KMSI ", error);
      }
    } else {
      console.error(
        "MSAL Error:: msalInstance is not defined at isMSALKmsiSession"
      );
    }
  }, [getTokenSilent, msalInstance, msalInstanceLoaded]);
  return {
    handleRedirectPromise,
    signout,
    signin,
    signinWithParam,
    getTokenSilent,
    editEmail,
    editPassword,
    msalEnabledFeatureFlag,
    msalInstanceLoaded,
    isMSALKmsiSession
  };
};
export {
  useMSALUtils
};
